import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import renderNotification from '../../helpers/notifications/renderNotification';
import { setImportFile, checkImportFile } from '../../redux/import/slice';
import { ATTACHMENT_MAX_SIZES } from '../../consts/consts';
import { selectImportFile } from '../../redux/import/selectors';
import {
  ImportUploaderDropzoneWrapper,
  ImportUploaderSectionWrapper,
  ImportUploaderText,
  ImportUploaderHighlightedText,
  ImportUploaderTextWrapper
} from '../ui/Layout/ImportLayout';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';

/**
 * This component is the first step of the import process. It is used for uploading the import file.
 * @param {function} onFileUpload
 * @param {File} importFile
 * @param {function} setImportFile - this function is used to upload the import file to the frontend (save it in redux).
 * @param {function} checkImportFile - this function is used to send the import file to the backend for checking.
 */
const ImportUploaderSectionComponent = ({
  onFileUpload = () => {},
  importFile,
  setImportFile,
  checkImportFile,
  shouldOpenFileDialog,
  onFileDialogOpened,
  hasError = false
}) => {
  const handleCheckImportFile = (file) => {
    checkImportFile({ file });
    onFileUpload();
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    setImportFile({ file });
  };

  const onDropRejected = (rejectedFiles) => {
    const errorCode = rejectedFiles[0].errors[0].code;
    switch (errorCode) {
      case 'file-invalid-type': {
        const errorMessage = NOTIFICATION_MESSAGES.message_attachment_invalid_file_type.replace(
          '$FILE_NAME',
          rejectedFiles[0].file.name
        );
        renderNotification({ type: 'error', message: errorMessage });
        break;
      }
      default:
        renderNotification({ type: 'error', message: 'Ein unbekannter Fehler ist aufgetreten!' });
    }
  };

  const options = {
    onDrop,
    onDropRejected,
    multiple: false,
    maxSize: ATTACHMENT_MAX_SIZES.default.byte,
    disabled: false,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
  };

  const { getRootProps, getInputProps, open } = useDropzone(options);

  useEffect(() => {
    if (importFile) {
      handleCheckImportFile(importFile);
    }
  }, [importFile]);

  useEffect(() => {
    if (shouldOpenFileDialog) {
      open();
      onFileDialogOpened();
    }
  }, [shouldOpenFileDialog, open, onFileDialogOpened]);

  const renderDropzone = () => (
    <ImportUploaderDropzoneWrapper $hasError={hasError} {...getRootProps()}>
      <input {...getInputProps()} />
      {hasError && <ImportUploaderText $hasError>Datei fehlt.</ImportUploaderText>}
      <ImportUploaderTextWrapper>
        <ImportUploaderText>Datei (*.xlsx) auf diese Fläche ziehen oder </ImportUploaderText>
        <ImportUploaderHighlightedText>Datei auswählen</ImportUploaderHighlightedText>
      </ImportUploaderTextWrapper>
    </ImportUploaderDropzoneWrapper>
  );

  return <ImportUploaderSectionWrapper>{renderDropzone()}</ImportUploaderSectionWrapper>;
};

ImportUploaderSectionComponent.displayName = 'ImportUploaderSection';

const mapDispatchToProps = {
  setImportFile,
  checkImportFile
};

const mapStateToProps = (state) => ({
  importFile: selectImportFile(state)
});

export const ImportUploaderSection = connect(mapStateToProps, mapDispatchToProps)(ImportUploaderSectionComponent);
